import React from "react";
import { ArrowLeft, Award, Shield, Music, Users, Briefcase, CheckCircle, Building2, FileText, HelpCircle } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Link } from "react-router-dom";

const AboutPage = () => {
  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden" data-testid="about-page">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <ArrowLeft size={14} />
          BACK TO HOME
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <Briefcase size={12} className="mr-2" />
            HERITAGE & INFRASTRUCTURE
          </Badge>

          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-6"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
          >
            ITZGREEN <span className="text-[rgb(218,255,1)]">MUSIC</span>
          </h1>
        </motion.div>

        {/* THE LINEAGE */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-6 sm:p-8 mb-8"
        >
          <h2
            className="text-[rgb(218,255,1)] text-lg font-bold mb-4 tracking-wide"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            THE LINEAGE
          </h2>
          <p
            className="text-[rgb(200,200,210)] text-base leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Built on the foundation of <strong className="text-white">ASCAP-award-winning hip-hop royalty</strong> and the architectural sound design of the legendary <strong className="text-white">"Triggaman" era</strong>, ItzGreen Music bridges foundational cultural legacy with modern boardroom execution. We do not operate as beatmakers; we are an <strong className="text-[rgb(218,255,1)]">institutional supplier</strong> of Premium Commercial Lifestyle and Authentic Cinematic Urban assets.
          </p>
        </motion.div>

        {/* THE LIABILITY SHIELD */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-[rgba(74,222,128,0.05)] border border-[rgba(74,222,128,0.3)] rounded-2xl p-6 sm:p-8 mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <Shield size={20} className="text-[rgb(74,222,128)]" />
            <h2
              className="text-[rgb(74,222,128)] text-lg font-bold tracking-wide"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              THE LIABILITY SHIELD
            </h2>
          </div>
          <p
            className="text-[rgb(200,200,210)] text-base leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Our core product is <strong className="text-white">infrastructure</strong>. Every asset delivered through the secure Supervisor Vault is <strong className="text-[rgb(74,222,128)]">100% human-produced</strong>, <strong className="text-[rgb(74,222,128)]">one-stop cleared</strong> (Master & Publishing), and <strong className="text-[rgb(74,222,128)]">fully reconciled</strong> across BMI, ASCAP, and The MLC. By strictly enforcing a <strong className="text-white">zero-uncleared-sample policy</strong>, we provide music supervisors with a frictionless pipeline and an absolute legal shield.
          </p>
        </motion.div>

        <Separator className="bg-[rgb(63,63,63)] my-10" />

        {/* Executive Profile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-6 sm:p-8 mb-12"
        >
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {/* Executive Photo */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-[rgba(218,255,1,0.3)]">
              <img 
                src="https://customer-assets.emergentagent.com/job_05bb89b9-bd12-4211-ba45-1fade7c1a42d/artifacts/lcp93jzw_IMG_20251123_150917_567.webp"
                alt="Leaver Hall - Founder & Executive Producer"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1">
              <h2
                className="text-white text-xl sm:text-2xl font-bold mb-1"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                LEAVER HALL
              </h2>
              <p
                className="text-[rgb(218,255,1)] text-xs tracking-[0.15em] font-medium mb-4"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                FOUNDER & EXECUTIVE PRODUCER
              </p>

              <div className="space-y-3 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                <div className="flex items-center gap-3">
                  <Award size={16} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(161,161,170)]">BMI Affiliated Publisher</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield size={16} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(161,161,170)]">Head of Rights Administration</span>
                </div>
                <div className="flex items-center gap-3">
                  <Music size={16} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(161,161,170)]">Specializing in Premium R&B & Commercial Lifestyle</span>
                </div>
              </div>
              
              {/* Link to full bio */}
              <Link
                to="/about-leaver-hall"
                className="inline-flex items-center gap-2 text-[rgb(218,255,1)] text-xs font-medium mt-4 hover:underline"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                View Full Executive Profile →
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Credit Structure */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <h3
            className="text-white text-lg font-bold mb-4"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            STANDARD CREDIT STRUCTURE
          </h3>
          <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 font-mono text-sm">
            <p className="text-[rgb(100,100,110)] mb-1">Executive Produced by:</p>
            <p className="text-white mb-3">Leaver Hall for ItzGreen Music</p>
            
            <p className="text-[rgb(100,100,110)] mb-1">Original Score & Sound Design:</p>
            <p className="text-white mb-3">ItzGreen Music</p>
            
            <p className="text-[rgb(100,100,110)] mb-1">Featured Artist:</p>
            <p className="text-[rgb(161,161,170)]">[As Applicable]</p>
          </div>
        </motion.div>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3
            className="text-white text-lg font-bold mb-6"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            THE ITZGREEN GUARANTEE
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: CheckCircle, title: "ONE-STOP CLEARANCE", desc: "100% Master & Publishing control. Single signature clears both sides." },
              { icon: Shield, title: "100% HUMAN AUTHORED", desc: "No AI-generated content. Every note composed by human artists." },
              { icon: Award, title: "BMI REGISTERED", desc: "All assets reconciled and registered with performance rights organizations." },
              { icon: Users, title: "ELITE TALENT ROSTER", desc: "Curated producers and vocalists delivering broadcast-quality content." },
              { icon: Briefcase, title: "FRICTIONLESS NETWORK ACCESS", desc: "Verified music supervisors and trailer agencies receive immediate, streamlined access to our 100% indemnified, One-Stop cleared catalog. Zero login drag, zero clearance friction." },
            ].map((item, i) => (
              <div
                key={i}
                className={`bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.06)] rounded-xl p-5 hover:border-[rgba(218,255,1,0.2)] transition-colors ${i === 4 ? 'sm:col-span-2' : ''}`}
              >
                <item.icon size={20} className="text-[rgb(218,255,1)] mb-3" />
                <h4 className="text-white text-sm font-semibold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {item.title}
                </h4>
                <p className="text-[rgb(161,161,170)] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        <Separator className="bg-[rgb(63,63,63)] my-10" />

        {/* Additional Resources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-10"
        >
          <h2
            className="text-white text-xl font-bold mb-6 text-center"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            ADDITIONAL RESOURCES
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link
              to="/corporate-updates"
              className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.06)] rounded-xl p-5 hover:border-[rgba(218,255,1,0.2)] transition-colors group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-[rgba(218,255,1,0.1)] rounded-lg p-2">
                  <Building2 size={16} className="text-[rgb(218,255,1)]" />
                </div>
                <h4 className="text-white text-sm font-semibold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  CORPORATE UPDATES
                </h4>
              </div>
              <p className="text-[rgb(161,161,170)] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Latest business developments, partnerships, and strategic announcements from ItzGreen Music leadership.
              </p>
            </Link>

            <Link
              to="/briefs"
              className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.06)] rounded-xl p-5 hover:border-[rgba(218,255,1,0.2)] transition-colors group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-[rgba(218,255,1,0.1)] rounded-lg p-2">
                  <FileText size={16} className="text-[rgb(218,255,1)]" />
                </div>
                <h4 className="text-white text-sm font-semibold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  ACTIVE BRIEFS
                </h4>
              </div>
              <p className="text-[rgb(161,161,170)] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Current sync licensing opportunities from Netflix, HBO Max, Apple, and other verified network partners.
              </p>
            </Link>

            <Link
              to="/faq"
              className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.06)] rounded-xl p-5 hover:border-[rgba(218,255,1,0.2)] transition-colors group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-[rgba(218,255,1,0.1)] rounded-lg p-2">
                  <HelpCircle size={16} className="text-[rgb(218,255,1)]" />
                </div>
                <h4 className="text-white text-sm font-semibold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  FAQ
                </h4>
              </div>
              <p className="text-[rgb(161,161,170)] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Comprehensive answers about licensing, artist roster, sync opportunities, and business operations.
              </p>
            </Link>
          </div>
        </motion.div>

        <Separator className="bg-[rgb(63,63,63)] my-10" />

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <p className="text-[rgb(161,161,170)] text-sm mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            Ready to work with ItzGreen Music?
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/catalog"
              className="inline-flex items-center gap-2 border-2 border-[rgb(218,255,1)] text-[rgb(218,255,1)] text-sm font-semibold tracking-[0.1em] px-6 py-3 rounded-xl hover:bg-[rgba(218,255,1,0.1)] transition-all duration-300"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              EXPLORE THE CATALOG
            </Link>
            <Link
              to="/licensing"
              className="inline-flex items-center gap-2 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-sm font-semibold tracking-[0.1em] px-6 py-3 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_25px_rgba(218,255,1,0.4)] transition-all duration-300"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              LICENSE OUR SOUND
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default AboutPage;
